// [claude-code 2026-03-24] Supabase client + Google OAuth for Electron deep link flow
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { getRuntimeApiBase } from "./runtime-api-base";
import { rememberCurrentForumInviteReturnUrl } from "./forum-invite-deeplink";
import {
  buildCanonicalWebAuthCallbackUrl,
  currentWebAuthReturnPath,
} from "./web-auth-routing";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabasePublicKey =
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const AUTH_CALLBACK_PATH = "/api/auth/supabase/callback";
const FALLBACK_AUTH_CALLBACK_BASE = "https://fintheon.fly.dev";
const DESKTOP_AUTH_BRIDGE_URL =
  import.meta.env.VITE_DESKTOP_AUTH_BRIDGE_URL ||
  "https://app.fintheon.io/auth/desktop/restore";

function toOrigin(base: string): string {
  try {
    const parsed = new URL(base);
    if (parsed.protocol === "http:" || parsed.protocol === "https:")
      return parsed.origin.replace(/\/$/, "");
  } catch {
    /* ignore */
  }
  return "";
}

let supabase: SupabaseClient | null = null;

if (supabaseUrl && supabasePublicKey && !supabasePublicKey.startsWith("<")) {
  supabase = createClient(supabaseUrl, supabasePublicKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: false, // We handle deep links manually
    },
  });
}

export { supabase };

function resolveAuthCallbackBase(): string {
  const runtimeBase = toOrigin(getRuntimeApiBase());
  if (runtimeBase) return runtimeBase;

  const envBase = import.meta.env.VITE_API_URL?.trim();
  if (envBase) {
    const normalizedEnvBase = toOrigin(envBase);
    if (normalizedEnvBase) return normalizedEnvBase;
  }

  if (typeof window !== "undefined" && window.location?.origin) {
    const normalizedWindowOrigin = toOrigin(window.location.origin);
    if (normalizedWindowOrigin) {
      return normalizedWindowOrigin;
    }
  }

  return FALLBACK_AUTH_CALLBACK_BASE;
}

function isDesktopRuntime(): boolean {
  return (
    typeof window !== "undefined" &&
    Boolean((window as Window & { electron?: unknown }).electron)
  );
}

function buildAuthCallbackUrl(): string {
  if (!isDesktopRuntime()) {
    return buildCanonicalWebAuthCallbackUrl(
      currentWebAuthReturnPath(rememberCurrentForumInviteReturnUrl()),
    );
  }

  const url = new URL(`${resolveAuthCallbackBase()}${AUTH_CALLBACK_PATH}`);
  if (isDesktopRuntime()) url.searchParams.set("desktop", "1");
  return url.toString();
}

/**
 * Start Google OAuth flow via Supabase.
 * Uses skipBrowserRedirect so we can open the URL in the system browser
 * (Electron) and receive the callback via fintheon:// deep link.
 */
export async function signInWithGoogle() {
  if (!supabase)
    throw new Error(
      "Supabase not configured — check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY or VITE_SUPABASE_PUBLISHABLE_KEY",
    );
  // Redirect to backend callback page which will relay the auth code
  // back to the Electron app via fintheon:// deep link
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: buildAuthCallbackUrl(),
      skipBrowserRedirect: true,
    },
  });
  if (error) throw error;
  return data;
}

export function buildDesktopAuthBridgeUrl(authUrl?: string): string {
  const bridge = new URL(DESKTOP_AUTH_BRIDGE_URL);
  bridge.searchParams.set("source", "desktop");
  if (authUrl) bridge.searchParams.set("authUrl", authUrl);
  return bridge.toString();
}

export async function signInWithMagicLink(email: string) {
  const response = await fetch(`${resolveAuthCallbackBase()}/api/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, magicLink: true }),
  });
  const payload = (await response.json().catch(() => ({}))) as {
    error?: string;
  };
  if (!response.ok) {
    throw new Error(
      payload.error === "email_not_approved"
        ? "This email has not been approved yet."
        : payload.error || "Unable to send login link.",
    );
  }
}

/** Get current session access token (for Bearer header to backend) */
export async function getAccessToken(): Promise<string | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

export async function signOut() {
  if (!supabase) return;
  await supabase.auth.signOut();
}
